/**
 * Attachment List Component
 * 
 * Displays a list of PDF attachments with drag-and-drop reordering.
 * Shows exhibit numbers, file details, upload information, and action buttons.
 * 
 * Features:
 * - Drag and drop reordering
 * - Exhibit number display
 * - File information (name, size, upload date, uploaded by)
 * - Edit and delete actions
 * - Modern, clean UI
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import classnames from 'classnames/bind';

import { Attachment } from '@INTERFACES/api/attachments';
import ImprovedDragAndDropList from '@COMPONENTS/SHARED/ImprovedDragAndDropList';

import styles from './AttachmentList.module.scss';

const cx: CX = classnames.bind(styles);

interface AttachmentListProps {
    attachments: Attachment[];
    onReorder: (reorderedAttachments: Attachment[]) => void;
    onEdit: (attachment: Attachment) => void;
    onDelete: (attachment: Attachment) => void;
    onDownload: (attachment: Attachment) => void;
    isLoading?: boolean;
    canEdit?: boolean;
}

function AttachmentList(props: AttachmentListProps) {
    const {
        attachments,
        onReorder,
        onEdit,
        onDelete,
        onDownload,
        isLoading = false,
        canEdit = true,
    } = props;

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState<string>('');

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'MMM d, yyyy h:mm a');
        } catch {
            return 'Invalid date';
        }
    };

    const handleEditStart = (attachment: Attachment) => {
        setEditingId(attachment.id);
        setEditingName(attachment.name);
    };

    const handleEditSave = (attachment: Attachment) => {
        if (editingName.trim() && editingName !== attachment.name) {
            onEdit({ ...attachment, name: editingName.trim() });
        }
        setEditingId(null);
        setEditingName('');
    };

    const handleEditCancel = () => {
        setEditingId(null);
        setEditingName('');
    };

    const handleKeyPress = (e: React.KeyboardEvent, attachment: Attachment) => {
        if (e.key === 'Enter') {
            handleEditSave(attachment);
        } else if (e.key === 'Escape') {
            handleEditCancel();
        }
    };

    const renderAttachmentItem = (attachment: Attachment, index: number, isDragging: boolean) => {
        const isCurrentlyEditing = editingId === attachment.id;
        const shouldShowAsDragging = isDragging && !isCurrentlyEditing;
        
        return (
            <div
                className={cx('attachment-item', {
                    'is-dragging': shouldShowAsDragging,
                    'is-editing': isCurrentlyEditing,
                })}
            >
                {/* Drag Handle */}
                {canEdit && (
                    <div className={cx('drag-handle')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 6h2v2H8V6zM8 10h2v2H8v-2zM8 14h2v2H8v-2zM14 6h2v2h-2V6zM14 10h2v2h-2v-2zM14 14h2v2h-2v-2z" fill="currentColor" />
                        </svg>
                    </div>
                )}

                {/* Exhibit Number */}
                <div className={cx('exhibit-number')}>
                    <span className={cx('exhibit-label')}>Exhibit</span>
                    <span className={cx('exhibit-value')}>{attachment.exhibit_number}</span>
                </div>

                {/* File Icon */}
                <div className={cx('file-icon')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path 
                            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" 
                            fill="currentColor"
                        />
                        <path d="M8 12H16V14H8V12ZM8 16H13V18H8V16Z" fill="currentColor" />
                    </svg>
                </div>

                {/* File Details */}
                <div className={cx('file-details')}>
                    <div className={cx('file-name-section')}>
                        {editingId === attachment.id ? (
                            <input
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onBlur={() => handleEditSave(attachment)}
                                onKeyDown={(e) => handleKeyPress(e, attachment)}
                                className={cx('name-input')}
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus
                            />
                        ) : (
                            <button
                                type="button"
                                className={cx('file-name')}
                                onClick={() => canEdit && handleEditStart(attachment)}
                                disabled={!canEdit}
                                title={canEdit ? 'Click to edit name' : attachment.name}
                            >
                                {attachment.name}
                            </button>
                        )}
                    </div>
                
                    <div className={cx('file-meta')}>
                        <span className={cx('file-size')}>{formatFileSize(attachment.file_size)}</span>
                        <span className={cx('meta-separator')}>•</span>
                        <span className={cx('upload-date')}>{formatDate(attachment.created_at)}</span>
                        <span className={cx('meta-separator')}>•</span>
                        <span className={cx('uploaded-by')}>
                            Uploaded by 
                            {' '}
                            {attachment.created_by.name}
                        </span>
                    </div>
                </div>

                {/* Actions */}
                <div className={cx('actions')}>
                    <button
                        type="button"
                        onClick={() => onDownload(attachment)}
                        className={cx('action-btn', 'download-btn')}
                        title="Download PDF"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 15L7 10H10V6H14V10H17L12 15ZM19 18V20H5V18H3V20C3 21.1 3.89 22 5 22H19C20.11 22 21 21.1 21 20V18H19Z" fill="currentColor" />
                        </svg>
                    </button>

                    {canEdit && (
                        <>
                            <button
                                type="button"
                                onClick={() => handleEditStart(attachment)}
                                className={cx('action-btn', 'edit-btn')}
                                title="Edit name"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor" />
                                </svg>
                            </button>

                            <button
                                type="button"
                                onClick={() => onDelete(attachment)}
                                className={cx('action-btn', 'delete-btn')}
                                title="Delete attachment"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill="currentColor" />
                                </svg>
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className={cx('attachment-list', 'loading')}>
                <div className={cx('loading-spinner')}>
                    <div className={cx('spinner')} />
                    <span>Loading attachments...</span>
                </div>
            </div>
        );
    }

    if (attachments.length === 0) {
        return (
            <div className={cx('attachment-list', 'empty')}>
                <div className={cx('empty-state')}>
                    <div className={cx('empty-icon')}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path 
                                d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" 
                                fill="currentColor"
                                opacity="0.3"
                            />
                        </svg>
                    </div>
                    <h3 className={cx('empty-title')}>No attachments yet</h3>
                    <p className={cx('empty-description')}>
                        Upload PDF files to add them as exhibits to this 
                        {' '}
                        {attachments[0]?.parent_type || 'item'}
                        .
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={cx('attachment-list')}>
            <div className={cx('list-header')}>
                <span className={cx('attachment-count')}>
                    {attachments.length}
                    {' '}
                    attachment
                    {attachments.length !== 1 ? 's' : ''}
                </span>
                {canEdit && (
                    <span className={cx('reorder-hint')}>
                        Drag items to reorder exhibits
                    </span>
                )}
            </div>

            <ImprovedDragAndDropList
                list={attachments}
                onDragEnd={onReorder}
                disabled={!canEdit || editingId !== null}
            >
                {renderAttachmentItem}
            </ImprovedDragAndDropList>
        </div>
    );
}

AttachmentList.propTypes = {
    attachments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        file_size: PropTypes.number.isRequired,
        exhibit_number: PropTypes.number.isRequired,
        created_at: PropTypes.string.isRequired,
        parent_type: PropTypes.string,
        created_by: PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
        }).isRequired,
    })).isRequired,
    onReorder: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    canEdit: PropTypes.bool,
};

AttachmentList.defaultProps = {
    isLoading: false,
    canEdit: true,
};

export default AttachmentList; 